import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environment';
import { storageAccountId } from '@frontendlib/common/utils/storage-account';
import { type TrustedImageSendersDto } from '@sharedlib/dtos/dto/accounts/account-settings.dto';
import { defer, map, type Observable, shareReplay, tap, throwError, timeout } from 'rxjs';

const REQUEST_TIMEOUT_MS = 10_000;
const CACHE_MAX_AGE_MS = 60_000;
const TRUST_CHANNEL = 'stch-trusted-image-senders';

/** Personal, server-backed consent shared by every mail reader in this document. */
@Injectable({ providedIn: 'root' })
export class TrustedImageSendersService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.baseUrl}/api/account-settings/trusted-image-senders`;
  private readonly state = signal<{ accountId: string | null; addresses: ReadonlySet<string> }>({
    accountId: null,
    addresses: new Set(),
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly settledFor = signal<string | null>(null);
  private readonly channel = this.openChannel();
  private requestedFor: string | null = null;
  private loadedAt = 0;
  private refreshAfter: string | null = null;
  private loadedFor: string | null = null;
  private loadingFor: string | null = null;

  public constructor() {
    const refresh = (): void => {
      if (this.requestedFor && this.requestedFor === storageAccountId()) this.load(true);
    };
    window.addEventListener('focus', refresh);
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<unknown>): void => {
        const data = event.data;
        if (typeof data === 'object' && data !== null && Reflect.get(data, 'accountId') === storageAccountId())
          refresh();
      };
    }
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('focus', refresh);
      this.channel?.close();
    });
  }

  /** Keep the privacy banner quiet until the first preference read settles, including failure. */
  public isReady(): boolean {
    const accountId = storageAccountId();
    return !accountId || this.settledFor() === accountId;
  }

  public isTrusted(address: string): boolean {
    const state = this.state();
    return !!address && state.accountId === storageAccountId() && state.addresses.has(address.trim().toLowerCase());
  }

  public load(force = false): void {
    const accountId = storageAccountId();
    if (!accountId) return;
    if (this.loadingFor === accountId) {
      if (force) this.refreshAfter = accountId;
      return;
    }
    if (!force && this.loadedFor === accountId && Date.now() - this.loadedAt < CACHE_MAX_AGE_MS) return;
    this.requestedFor = accountId;
    this.loadingFor = accountId;
    this.http
      .get<TrustedImageSendersDto>(this.url)
      .pipe(timeout(REQUEST_TIMEOUT_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ addresses }) => {
          this.merge(accountId, addresses);
          if (storageAccountId() === accountId) {
            this.loadedFor = accountId;
            this.loadedAt = Date.now();
          }
          this.finishRead(accountId);
        },
        // Fail closed on the initial read, retaining previously confirmed trust on a failed refresh.
        error: () => this.finishRead(accountId),
      });
  }

  private finishRead(accountId: string): void {
    if (storageAccountId() === accountId) this.settledFor.set(accountId);
    if (this.loadingFor === accountId) this.loadingFor = null;
    if (this.refreshAfter === accountId) {
      this.refreshAfter = null;
      if (storageAccountId() === accountId) this.load(true);
    }
  }

  public trust(address: string): Observable<void> {
    return defer(() => {
      const accountId = storageAccountId();
      if (!accountId) return throwError(() => new Error('Sign in to trust a sender'));
      return this.http.put<TrustedImageSendersDto>(this.url, { address: address.trim().toLowerCase() }).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        tap(({ addresses }) => {
          this.merge(accountId, addresses);
          // Other readers re-fetch from the authenticated API; broadcasts never grant consent.
          try {
            this.channel?.postMessage({ accountId });
          } catch {
            /* Focus and cache expiry still refresh. */
          }
        }),
        map(() => undefined),
      );
    }).pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  private openChannel(): BroadcastChannel | null {
    try {
      return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(TRUST_CHANNEL);
    } catch {
      return null;
    }
  }

  private merge(accountId: string, addresses: string[]): void {
    if (storageAccountId() !== accountId) return;
    // A GET or another PUT may finish after a newer trust write: never lose confirmed additions.
    this.state.update((current) => ({
      accountId,
      addresses: new Set([...(current.accountId === accountId ? current.addresses : []), ...addresses]),
    }));
  }
}
